/**
 * SForm 采集适配器（A 阶段：SFormCollector）
 *
 * buildFormState 采集算法（A/C 共用，基于 bedrock FormInstance 公共 API）：
 * - 字段枚举：getFieldsError() 无参 → FieldError[]，每项 .name 枚举字段（唯一稳定公共途径，无 getFieldEntities）
 * - 逐字段查询：getFieldValue/getFieldError/isFieldTouched/isFieldValidating
 * - 元信息（label/type/required/...）：来自 FieldMetaProvider（SForm formItemMap 运行时求值）
 *
 * C 阶段：BedrockCollector 下沉到 bedrock 源码，采集函数体不动（技术方案 §3.3）。
 */

import {
  BedrockFormInstance,
  FieldMeta,
  FieldTestState,
  FormTestState,
  SFormComponentClass,
  SubmitState,
  FieldType,
} from '../types';

/** InternalNamePath → 字符串 key（与 testId 派生的 join('.') 对齐） */
const pathToKey = (name: string | number | (string | number)[]): string =>
  Array.isArray(name) ? name.join('.') : String(name);

/** SFormComponentClass → FieldType 映射（技术方案 §5.3 实测） */
const COMPONENT_CLASS_MAP: Record<SFormComponentClass, FieldType> = {
  input: 'input',
  textarea: 'textarea',
  textareapropover: 'textarea',
  select: 'select',
  treeSelect: 'treeSelect',
  radio: 'radio',
  checkbox: 'checkbox',
  singleCheckbox: 'checkbox',
  inputNumber: 'number',
  inputPercent: 'number',
  currencyType: 'number',
  datepicker: 'date',
  datePicker: 'date',
  upload: 'upload',
  s3upload: 'upload',
  switch: 'switch',
  editor: 'custom',
};

export const mapComponentClass = (cls?: SFormComponentClass): FieldType =>
  (cls && COMPONENT_CLASS_MAP[cls]) || 'custom';

/** 敏感字段过滤（S2：序列化前过滤 password/token/secret，防泄露） */
const SENSITIVE_KEY_RE = /password|token|secret/i;
const filterSensitive = (values: Record<string, unknown>): Record<string, unknown> => {
  const out: Record<string, unknown> = {};
  for (const key of Object.keys(values)) {
    out[key] = SENSITIVE_KEY_RE.test(key) ? '***' : values[key];
  }
  return out;
};

/**
 * 构建 FormTestState 快照（非轮询，S1：消费方按需调用）
 *
 * @param formId 业务页面显式传入
 * @param instance bedrock FormInstance（getInstance() 返回值，须 = SFormItem useFormInstance 同一引用）
 * @param metas 字段元信息（FieldMetaProvider 供给）
 * @param submitState submit 状态（I1：业务页面注入，缺省按 enabled=true/loading=false）
 */
export const buildFormState = (
  formId: string,
  instance: BedrockFormInstance,
  metas: FieldMeta[],
  submitState?: SubmitState,
): FormTestState => {
  const fieldErrors = instance.getFieldsError();
  const fields: Record<string, FieldTestState> = {};

  for (const fe of fieldErrors) {
    const name = pathToKey(fe.name);
    const meta = metas.find((m) => m.fieldKey === name);
    const touched = instance.isFieldTouched(fe.name);
    fields[name] = {
      fieldKey: name,
      label: meta?.label,
      type: meta ? mapComponentClass(meta.componentClass) : 'custom',
      value: instance.getFieldValue(fe.name),
      visible: meta?.visible ?? true,
      disabled: meta?.disabled ?? false,
      readonly: meta?.readonly ?? false,
      required: meta?.required ?? false,
      touched,
      dirty: touched, // bedrock 无独立 dirty，dirty = touched
      loading: instance.isFieldValidating(fe.name),
      options: meta?.options,
      errors: fe.errors,
      testId: `${formId}.${name}`,
      dependsOn: meta?.dependsOn,
    };
  }

  return {
    formId,
    values: filterSensitive(instance.getFieldsValue()), // I2：无参仅已注册字段 + S2 敏感过滤
    fields,
    validation: {
      valid: fieldErrors.every((fe) => !fe.errors.length),
      errors: {},
    },
    submit: submitState ?? { enabled: true, loading: false },
    meta: {
      initialized: true,
      loading: false,
      lastUpdatedAt: Date.now(),
    },
  };
};
